import { logger } from 'module/logger';
import { showToast } from 'module/ui/components/molecules/toasts/toasts';
import { ToastType } from 'module/ui/components/molecules/toasts/types';
import { Editor } from '@tiptap/core';
import { PagesAPI } from 'api/pages';

function getSectionIdAtPos(editor: Editor, pos: number): number | null {
  const resolvedPos = editor.state.doc.resolve(pos);

  for (let depth = resolvedPos.depth; depth > 0; depth--) {
    const sectionId = resolvedPos.node(depth).attrs?.documentSectionId;

    if (sectionId != null) {
      return Number(sectionId);
    }
  }

  return null;
}

export async function uploadAndInsertImageAt(
  editor: Editor,
  file: File,
  documentId: number,
  pos: number,
): Promise<number> {
  try {
    const { attachmentId, presignedPostUrl, formFields, presignedGetUrl } =
      await PagesAPI.presignImageUpload(documentId, file.name, file.type);

    const formData = new FormData();

    for (const [key, value] of Object.entries(formFields)) {
      formData.append(key, value);
    }
    formData.append('file', file);

    const response = await fetch(presignedPostUrl, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `S3 upload failed: ${response.status} ${response.statusText}`,
      );
    }

    const viewUrl = presignedGetUrl;

    editor
      .chain()
      .insertContentAt(pos, {
        type: 'image',
        attrs: { src: viewUrl, attachmentId, alt: file.name },
      })
      .run();

    const nextPos = editor.state.selection.to;

    const documentSectionId = getSectionIdAtPos(editor, pos);

    //Currently we are just dumping every image as a node in the db.
    void PagesAPI.createDocumentNode(documentId, {
      nodeType: 'image',
      documentSectionId,
      attrs: { attachmentId, alt: file.name },
    }).catch((err) => {
      logger.error('[image-upload] failed to persist node', {
        error: err,
        attachmentId,
      });
    });

    return nextPos;
  } catch (err) {
    logger.error('Image upload failed', { error: err, fileName: file.name });
    showToast({
      toastContainer: { current: document.body },
      content: 'Failed to upload image',
      variant: ToastType.Error,
    });

    return pos;
  }
}

export async function uploadAndInsertImages(
  editor: Editor,
  files: File[],
  documentId: number,
  startPos: number,
): Promise<void> {
  let pos = startPos;

  for (const file of files) {
    pos = await uploadAndInsertImageAt(editor, file, documentId, pos);
  }

  editor.commands.focus();
}

export function pickImageFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');

    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg';
    input.multiple = true;
    input.style.display = 'none';

    input.onchange = () => {
      const files = input.files ? Array.from(input.files) : [];

      document.body.removeChild(input);
      resolve(files);
    };

    const cleanup = () => {
      window.removeEventListener('focus', cleanup);
      setTimeout(() => {
        if (input.parentNode) {
          document.body.removeChild(input);
          resolve([]);
        }
      }, 300);
    };

    window.addEventListener('focus', cleanup, { once: true });

    document.body.appendChild(input);
    input.click();
  });
}
